import { audioRecovery } from '../../utils/audioRecovery';
import React, { useEffect, useRef, useState } from 'react';
import AudioPlayer from '../ui/AudioPlayer';
import HelpButton from '../ui/HelpButton';
import MicButton from '../ui/MicButton';
import TypingIndicator from '../ui/TypingIndicator';
import type { HelpResponse } from '../../api/help';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import type { Message } from '../../hooks/useLessonState';
import type { TurnCardProps } from '../../turncard/types';
import { ContextRows } from './ContextRow';
import FeedbackView from './FeedbackView';
import styles from './TurnCard.module.css';

/** Failed plays of a loaded clip before the learner is offered a skip. */

export type TurnCardComponentProps = TurnCardProps & {
  /** The character's line this turn answers, for its replay clip. */
  contextAudios?: Message[];
};

/**
 * The single card for one turn, with its controls: who is speaking, the line,
 * audio, "What did they say?", feedback on the last attempt, the help ladder,
 * "I need help", and the mic. It renders the view model and never talks to the API.
 */
export const TurnCard: React.FC<TurnCardComponentProps> = ({
  mode,
  vm,
  paused,
  onAudioEnded,
  onRetryAudio,
  onSkipAudio,
  onAskHelp,
  onLineHeard,
  onStartRecording,
  onStopRecording,
  onTranscript,
  onSttError,
  languageCode,
  phraseHints,
  runId,
  stepIndex,
  awaitingInput,
  onNeedHelp,
  contextAudios,
}) => {
  const audio = vm.audioMessage;
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const [playFailures, setPlayFailures] = useState(0);
  const [played, setPlayed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpAnswer, setHelpAnswer] = useState<HelpResponse | null>(null);
  const [helpError, setHelpError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
    };
  }, []);

  // A new turn starts clean: its own recovery state and its own explanation.
  const turnKey = audio?.id ?? `${vm.kind}-${vm.partId ?? ''}`;
  useEffect(() => {
    setRetrying(false);
    setRetryFailed(false);
    setPlayFailures(0);
    setPlayed(false);
    setHelpOpen(false);
    setHelpAnswer(null);
    setHelpError(null);
    setHelpLoading(false);
  }, [turnKey]);

  const isLine = !audio || !!audio.audioOnly;
  const helpFormId =
    !audio || audio.audioOnly ? null : (audio.audioFormId ?? null);
  const showHelp = vm.canAskHelp && !!onAskHelp && (isLine || !!helpFormId);
  const helpEnabled = audio?.audioOnly
    ? played || !audio.audioUrls?.natural
    : true;

  const handleHelp = (): void => {
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    setHelpOpen(true);
    if (helpAnswer || helpLoading || !onAskHelp) return;
    setHelpError(null);
    setHelpLoading(true);
    onAskHelp(helpFormId)
      .then((answer) => {
        if (mountedRef.current) setHelpAnswer(answer);
      })
      .catch((error: unknown) => {
        if (!mountedRef.current) return;
        setHelpError(
          error instanceof Error && error.message
            ? error.message
            : INTERFACE_TEXT.helpFailed
        );
      })
      .finally(() => {
        if (mountedRef.current) setHelpLoading(false);
      });
  };

  const helpButton = showHelp ? (
    <HelpButton
      onClick={handleHelp}
      disabled={!helpEnabled}
      expanded={helpOpen}
    />
  ) : null;

  const handleRetry = (): void => {
    if (!audio || !onRetryAudio) return;
    setRetrying(true);
    void onRetryAudio(audio.id).then((ok) => {
      if (!mountedRef.current) return;
      setRetrying(false);
      if (!ok) setRetryFailed(true);
    });
  };

  const hasPlayer = !!audio?.audioUrls?.natural;
  const notice = audio ? audioRecovery(audio, retryFailed, playFailures) : null;
  const showRecovery = !!notice && (vm.isCurrentPhrase || !!audio?.audioOnly);
  const canRetry =
    !!notice?.canRetry &&
    audio?.audioUnavailable === 'fetch_failed' &&
    !!onRetryAudio;

  const speaker = vm.speaker === 'them' ? styles.them : styles.you;
  const who =
    vm.kind === 'model'
      ? INTERFACE_TEXT.tcWhoModel
      : vm.kind === 'speak'
        ? INTERFACE_TEXT.tcWhoTurn
        : vm.speaker === 'them'
          ? INTERFACE_TEXT.tcWhoThem
          : INTERFACE_TEXT.tcWhoYou;
  const mainText = vm.kind === 'nospeech' ? '' : vm.text;
  const showNeedHelp = vm.canGetHelp && !!onNeedHelp;
  const ladder = vm.ladder;

  return (
    <div className={styles.wrap} data-testid="turncard" data-kind={vm.kind}>
      {vm.beat && <p className={styles.beat}>{vm.beat}</p>}
      <ContextRows contexts={vm.contexts} audios={contextAudios} />
      <section className={`${styles.turn} ${speaker}`}>
        <div className={styles.whoRow}>
          <span className={styles.who}>{who}</span>
          {vm.label && <span className={styles.tag}>{vm.label}</span>}
        </div>
        {mainText && (
          <h2
            className={`${styles.en} ${vm.kind === 'model' || vm.kind === 'speak' ? styles.narration : ''}`}
          >
            {mainText}
          </h2>
        )}
        {vm.targetText && (
          <p className={styles.el} data-testid="turncard-target">
            {vm.targetText}
          </p>
        )}
        {vm.meaning && (
          <p className={styles.meaning} data-testid="turncard-meaning">
            {vm.meaning}
          </p>
        )}
        {vm.note && <p className={styles.note}>{vm.note}</p>}

        {ladder && (
          <div className={styles.ladder} data-testid="turncard-ladder">
            {ladder.meaning && (
              <p data-testid="turncard-ladder-meaning">
                <span className={styles.ladderLabel}>
                  {INTERFACE_TEXT.tcLadderMeaningLabel}
                </span>{' '}
                {ladder.meaning}
              </p>
            )}
            {ladder.audioUrls?.natural && !hasPlayer && (
              <div className={styles.tools}>
                <AudioPlayer
                  audioUrl={ladder.audioUrls.natural}
                  slowerAudioUrl={ladder.audioUrls.slow}
                />
              </div>
            )}
            {ladder.cue && !ladder.phrase && (
              <p data-testid="turncard-ladder-cue">
                <span className={styles.ladderLabel}>
                  {INTERFACE_TEXT.tcLadderCueLabel}
                </span>{' '}
                {ladder.cue}
              </p>
            )}
          </div>
        )}

        {hasPlayer && audio && (
          <div className={styles.tools}>
            <AudioPlayer
              audioUrl={audio.audioUrls!.natural}
              slowerAudioUrl={
                audio.audioOnly ? undefined : audio.audioUrls!.slow
              }
              onEnded={() => {
                if (vm.isCurrentPhrase) onAudioEnded?.(audio.id);
                if (vm.kind === 'persona') onLineHeard?.(audio.id);
              }}
              onPlay={() => setPlayed(true)}
              onError={() => {
                setPlayFailures((n) => n + 1);
                // A line that cannot play must not hold the check-in; its notice offers Retry.
                if (vm.kind === 'persona') onLineHeard?.(audio.id);
              }}
              autoPlay={audio.autoPlay}
              extraControls={helpButton}
              shortcuts
            />
          </div>
        )}
        {!hasPlayer && helpButton && (
          <div className={styles.tools}>{helpButton}</div>
        )}

        {showHelp && helpOpen && (
          <div
            className={styles.panel}
            role="region"
            aria-label={INTERFACE_TEXT.helpPanelHeading}
            data-testid="turncard-help-panel"
          >
            <span className={styles.panelHeading}>
              {INTERFACE_TEXT.helpPanelHeading}
            </span>
            {helpLoading && <p>{INTERFACE_TEXT.helpLoading}</p>}
            {helpError && <p role="alert">{helpError}</p>}
            {helpAnswer && (
              <p data-testid="turncard-help-meaning">{helpAnswer.meaning}</p>
            )}
            <button
              type="button"
              className={styles.dismiss}
              aria-label={INTERFACE_TEXT.helpDismissLabel}
              onClick={() => setHelpOpen(false)}
            >
              ×
            </button>
          </div>
        )}

        {showRecovery && notice && audio && (
          <div className={styles.recovery} data-testid="audio-recovery">
            <p className={styles.recoveryText}>{notice.text}</p>
            <div className={styles.tools}>
              {canRetry && (
                <button
                  type="button"
                  className={styles.pill}
                  disabled={retrying}
                  onClick={handleRetry}
                >
                  {audio.audioOnly
                    ? INTERFACE_TEXT.tcAudioRetryShort
                    : INTERFACE_TEXT.tcAudioRetry}
                </button>
              )}
              {notice.canSkip && (
                <button
                  type="button"
                  className={styles.pill}
                  onClick={() => onSkipAudio?.(audio.id)}
                >
                  {INTERFACE_TEXT.tcAudioSkip}
                </button>
              )}
            </div>
          </div>
        )}

        {vm.help.length > 0 && (
          <div className={styles.helpLines} data-testid="turncard-help-lines">
            {vm.help.map((line, i) => (
              <p key={`${i}-${line}`}>{line}</p>
            ))}
          </div>
        )}

        <FeedbackView
          feedback={vm.feedback}
          kind={vm.kind}
          fallbackText={vm.kind === 'nospeech' ? vm.text : undefined}
        />

        {vm.kind === 'working' && (
          <div
            className={styles.working}
            role="status"
            aria-label={INTERFACE_TEXT.tcWorking}
          >
            <TypingIndicator />
          </div>
        )}
      </section>

      <div className={styles.dock}>
        <MicButton
          onStartRecording={onStartRecording}
          onStopRecording={onStopRecording}
          onTranscript={onTranscript}
          onSttError={onSttError}
          languageCode={languageCode}
          phraseHints={phraseHints}
          runId={runId}
          stepIndex={stepIndex}
          awaitingInput={awaitingInput && !paused && vm.kind !== 'persona'}
          disabled={paused}
          shortcut
        />
        {showNeedHelp && (
          <button
            type="button"
            className={styles.needHelp}
            onClick={onNeedHelp}
            data-mode={mode}
          >
            {INTERFACE_TEXT.tcNeedHelp}
          </button>
        )}
      </div>
    </div>
  );
};

export default TurnCard;
